import { useEffect, useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { IpRangePickerUiOptions } from './schema';

type Props = FieldExtensionComponentProps<
  string | undefined,
  IpRangePickerUiOptions
>;

export function isValidIpv4Cidr(value: string): boolean {
  const parts = value.split('/');
  if (parts.length !== 2 || !/^(0|[1-9]\d?)$/.test(parts[1])) return false;
  const prefixLength = Number(parts[1]);
  if (prefixLength > 32) return false;
  const octets = parts[0].split('.');
  return (
    octets.length === 4 &&
    octets.every(
      octet => /^(0|[1-9]\d{0,2})$/.test(octet) && Number(octet) <= 255,
    )
  );
}

export const validateIpRange = (
  value: string | undefined,
  validation: { addError: (message: string) => void },
) => {
  if (value && !isValidIpv4Cidr(value)) {
    validation.addError(
      'Enter a valid IPv4 range in CIDR notation, such as 10.128.32.0/27',
    );
  }
};

const NETWORK_RANGE_FAILURE_MESSAGE =
  'Failed to get the network range. Please connect with the networking team and get the custom IP range.';

export const IpRangePicker = (props: Props) => {
  const {
    onChange,
    schema: { title = 'IP range', description = 'Allocate an IP range' },
    required,
    uiSchema,
    rawErrors,
    formData,
    formContext,
  } = props;
  const options = uiSchema['ui:options'] ?? {};
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const sizes = options.sizes ?? [];
  const regionBaseMap = useMemo(
    () => options.regionBaseMap ?? {},
    [options.regionBaseMap],
  );
  const regions = useMemo(() => Object.keys(regionBaseMap), [regionBaseMap]);
  const sourceRegion = options.regionField
    ? String(formContext?.formData?.[options.regionField] ?? '')
    : '';
  const applicationField = options.applicationField ?? 'application';
  const application = String(formContext?.formData?.[applicationField] ?? '');
  const [selectedRegion, setSelectedRegion] = useState(
    options.defaultRegion ?? regions[0] ?? '',
  );
  const region = sourceRegion || selectedRegion;
  const base = regionBaseMap[region] ?? '';
  const [size, setSize] = useState(sizes[0]?.size ?? '');
  const [useCustomMask, setUseCustomMask] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const [customMask, setCustomMask] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState(formData ?? '');
  const mask = useCustomMask
    ? customMask.replace(/^\//, '')
    : sizes.find(item => item.size === size)?.mask.replace(/^\//, '') ?? '';

  const handleManualEntryChange = (checked: boolean) => {
    setManualEntry(checked);
    setError(undefined);
    if (!checked) {
      setResult('');
      onChange(undefined);
    }
  };

  useEffect(() => {
    setResult(formData ?? '');
  }, [formData]);

  const requestRange = async () => {
    if (!base) {
      setError(
        `No address pool is configured for region "${region || '(empty)'}".`,
      );
      return;
    }
    if (!/^([1-9]|[12]\d|3[0-2])$/.test(mask)) {
      setError('Enter a valid IPv4 prefix length from 1 to 32.');
      return;
    }

    setLoading(true);
    setError(undefined);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();
      const response = await fetch(`${baseUrl}/ip-range/allocate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          base,
          mask,
          title: options.title,
          desc: options.desc ?? application,
        }),
      });
      if (!response.ok) {
        throw new Error(
          `Allocation service returned ${response.status} ${response.statusText}`,
        );
      }
      const data: unknown = await response.json();
      const cidr =
        data && typeof data === 'object' && 'network' in data
          ? (data as { network?: unknown }).network
          : undefined;
      if (typeof cidr !== 'string' || cidr.trim().length === 0) {
        throw new Error(NETWORK_RANGE_FAILURE_MESSAGE);
      }
      if (!isValidIpv4Cidr(cidr)) {
        throw new Error(
          'The allocation service response did not contain a valid network CIDR.',
        );
      }
      setResult(cidr);
      onChange(cidr);
    } catch (err) {
      if (err instanceof TypeError) {
        setError(NETWORK_RANGE_FAILURE_MESSAGE);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(NETWORK_RANGE_FAILURE_MESSAGE);
      }
      setResult('');
      onChange(undefined);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {!options.regionField && (
        <FormControl variant="outlined" fullWidth margin="normal" required>
          <InputLabel id="ip-range-region-label">Region</InputLabel>
          <Select
            labelId="ip-range-region-label"
            label="Region"
            value={selectedRegion}
            onChange={event => {
              setSelectedRegion(String(event.target.value));
              setResult('');
              onChange(undefined);
            }}
          >
            {regions.map(item => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {base
              ? `Address pool: ${base}`
              : 'Configure regionBaseMap in ui:options'}
          </FormHelperText>
        </FormControl>
      )}
      {options.regionField && !base && (
        <Typography color="error" variant="body2">
          No address pool is configured for region "{region || '(empty)'}".
        </Typography>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={manualEntry}
            onChange={event => handleManualEntryChange(event.target.checked)}
            color="primary"
          />
        }
        label="Enter range manually"
      />
      {manualEntry ? (
        <TextField
          fullWidth
          variant="outlined"
          margin="normal"
          label="IP range (CIDR)"
          placeholder="10.128.32.0/27"
          value={result}
          onChange={event => {
            const value = event.target.value;
            setResult(value);
            setError(undefined);
            onChange(value || undefined);
          }}
          required={required}
          error={!!rawErrors?.length || !!error}
          helperText={
            error ?? rawErrors?.[0] ?? 'Enter an IPv4 range in CIDR notation.'
          }
        />
      ) : (
        <>
          <FormControl variant="outlined" fullWidth margin="normal">
            <InputLabel id="ip-range-size-label">T-shirt size</InputLabel>
            <Select
              labelId="ip-range-size-label"
              label="T-shirt size"
              value={size}
              disabled={useCustomMask}
              onChange={event => setSize(String(event.target.value))}
            >
              {sizes.map(item => (
                <MenuItem key={item.size} value={item.size}>
                  {item.size} (/{item.mask})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={useCustomMask}
                onChange={event => setUseCustomMask(event.target.checked)}
                color="primary"
              />
            }
            label="Other (enter a custom mask)"
          />
          {useCustomMask && (
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Custom mask"
              placeholder="27 or /27"
              value={customMask}
              onChange={event => setCustomMask(event.target.value)}
              helperText="Enter a prefix length from 1 to 32."
            />
          )}
          <Box mt={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={requestRange}
              disabled={
                loading ||
                !base ||
                (!useCustomMask && !mask) ||
                (useCustomMask && !customMask)
              }
            >
              {loading ? 'Allocating…' : 'Get IP range'}
            </Button>
          </Box>
        </>
      )}
      {!manualEntry && (
        <TextField
          fullWidth
          variant="outlined"
          margin="normal"
          label={title}
          value={result}
          required={required}
          InputProps={{ readOnly: true }}
          error={!!rawErrors?.length || !!error}
          helperText={error ?? rawErrors?.[0] ?? description}
        />
      )}
    </Box>
  );
};
